// Theme persistence + application. Colours for each theme live in SASS
// (_theme-light.sass / _theme-dark.sass); this only decides which one is
// active by toggling the data-theme attribute on <html> and remembering the
// choice in localStorage. Keeping it framework-free means index.tsx can apply
// the stored theme before React mounts (no flash of the wrong theme).

export type AppTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "kannact-theme";

const THEMES: readonly AppTheme[] = ["light", "dark"];

export const isAppTheme = (value: unknown): value is AppTheme =>
	typeof value === "string" && (THEMES as readonly string[]).includes(value);

/** Read the persisted theme, defaulting to light when absent/invalid. */
export const getStoredTheme = (): AppTheme => {
	try {
		const stored = localStorage.getItem(THEME_STORAGE_KEY);
		if (isAppTheme(stored)) return stored;
	} catch {
		// localStorage may be unavailable (private mode, blocked cookies) — ignore.
	}
	return "light";
};

export const persistTheme = (theme: AppTheme): void => {
	try {
		localStorage.setItem(THEME_STORAGE_KEY, theme);
	} catch {
		// Persistence is best-effort; the in-memory theme still applies this session.
	}
};

/** Reflect the theme on <html> so the :root[data-theme="…"] variables take effect. */
export const applyThemeToDocument = (theme: AppTheme): void => {
	document.documentElement.setAttribute("data-theme", theme);
};
